import React, { ReactNode } from 'react';
import { DateField, ImageField, ShowButton, TextField } from 'react-admin';
import CustomizableGrid from '../components/CustomizableGrid';
import { ColumnConfig } from '../hooks/useColumnReorder';
import { makeStyles } from '@material-ui/core/styles';
import CountryField from '../components/CountryField';
import UserReferenceField from '../components/UserReferenceField';

const useStyles = makeStyles({
    logoContainer: {
        '& img': {
            height: 50,
            width: 50,
            objectFit: 'contain',
        },
    },
});

const PartnerList = ({ permissions = [], ...props }) => {
    const classes = useStyles();

    // Define default columns configuration
    const defaultColumns: ColumnConfig[] = [
        { id: 'serial', label: 'Serial', visible: true, order: 0 },
        { id: 'id', label: 'ID', visible: true, order: 1 },
        { id: 'logo', label: 'Logo', visible: true, order: 2 },
        { id: 'name', label: 'Name', visible: true, order: 3 },
        { id: 'country', label: 'Country', visible: true, order: 4 },
        { id: 'createdAt', label: 'Created', visible: true, order: 5 },
        { id: 'admin', label: 'Admin', visible: true, order: 6 },
        { id: 'show', label: 'Show', visible: true, order: 7 },
    ];

    // Function to render column based on column ID
    const renderColumn = (columnId: string): ReactNode => {
        switch (columnId) {
            case 'serial':
                return (
                    <TextField
                        key={columnId}
                        source="serial"
                        sortable={false}
                    />
                );
            case 'id':
                return <TextField key={columnId} source="id" />;
            case 'logo':
                return (
                    <ImageField
                        key={columnId}
                        source="logoUrl"
                        label="Logo"
                        className={classes.logoContainer}
                    />
                );
            case 'name':
                return <TextField key={columnId} source="name" />;
            case 'country':
                return <CountryField key={columnId} />;
            case 'createdAt':
                return (
                    <DateField
                        key={columnId}
                        source="createdAt"
                        label="Created"
                    />
                );
            case 'admin':
                return <UserReferenceField key={columnId} label="Admin" />;
            case 'show':
                return <ShowButton key={columnId} />;
            default:
                return null;
        }
    };

    // Ensure we have all required props, but avoid duplicates with what might be in props
    const combinedProps = {
        title: 'Partners',
        permissions,
        defaultColumns,
        storageKey: 'partnerListColumns',
        renderColumn,
        sort: { field: 'id', order: 'DESC' },
        ...props,
        // Ensure these are set if not in props
        resource: props.resource || 'partners',
        location: props.location || { search: '' },
        hasShow: props.hasShow !== undefined ? props.hasShow : true,
    };

    return <CustomizableGrid {...combinedProps} />;
};

PartnerList.defaultProps = {
    resource: 'partners',
    location: { search: '' },
};

export default PartnerList;
